// src/frontend_react/src/components/ui/Stack/Stack.js
import React from 'react';
import styles from './Stack.module.css';

const getSpacingValue = (spacingToken) => {
  if (spacingToken === undefined || spacingToken === null) return undefined;
  if (typeof spacingToken === 'number') return `calc(var(--cipher-spacing-unit, 8px) * ${spacingToken})`;
  if (typeof spacingToken === 'string') {
    if (['xs', 'sm', 'md', 'lg', 'xl'].includes(spacingToken)) {
      return `var(--cipher-spacing-${spacingToken})`;
    }
    return spacingToken;
  }
  return undefined;
};

const Stack = React.forwardRef((
  {
    as: Component = 'div',
    children,
    className = '',
    style: incomingStyle = {},
    divider,
    direction: propDirection = 'column',
    spacing: propSpacing,
    alignItems: propAlignItems,
    justifyContent: propJustifyContent,
    wrap: propWrap = 'nowrap',
    p, pt, pb, pl, pr, px, py,
    m, mt, mb, ml, mr, mx, my,
    width, height, minWidth, minHeight, maxWidth, maxHeight,
    border, borderTop, borderBottom, borderLeft, borderRight,
    borderColor, borderWidth, borderStyle, borderRadius,
    bg, backgroundColor,
    color,
    display,
    flex, flexGrow, flexShrink, flexBasis,
    // --- FIX: Add alignSelf to the list of destructured props ---
    alignSelf, 
    position, top, bottom, left, right, zIndex,
    overflow, overflowX, overflowY,
    boxShadow,
    textAlign,
    ...htmlAttributes
  }, ref) => {

  const generatedStyles = {};

  generatedStyles.display = display || 'flex';
  generatedStyles.flexDirection = propDirection;
  if (propSpacing !== undefined) generatedStyles.gap = getSpacingValue(propSpacing);
  if (propAlignItems) generatedStyles.alignItems = propAlignItems;
  if (propJustifyContent) generatedStyles.justifyContent = propJustifyContent;
  if (propWrap) generatedStyles.flexWrap = propWrap;

  if (p !== undefined) generatedStyles.padding = getSpacingValue(p);
  if (pt !== undefined) generatedStyles.paddingTop = getSpacingValue(pt);
  if (pb !== undefined) generatedStyles.paddingBottom = getSpacingValue(pb);
  if (pl !== undefined) generatedStyles.paddingLeft = getSpacingValue(pl);
  if (pr !== undefined) generatedStyles.paddingRight = getSpacingValue(pr);
  if (px !== undefined) { generatedStyles.paddingLeft = getSpacingValue(px); generatedStyles.paddingRight = getSpacingValue(px); }
  if (py !== undefined) { generatedStyles.paddingTop = getSpacingValue(py); generatedStyles.paddingBottom = getSpacingValue(py); }

  if (m !== undefined) generatedStyles.margin = getSpacingValue(m);
  if (mt !== undefined) generatedStyles.marginTop = getSpacingValue(mt);
  if (mb !== undefined) generatedStyles.marginBottom = getSpacingValue(mb);
  if (ml !== undefined) generatedStyles.marginLeft = getSpacingValue(ml);
  if (mr !== undefined) generatedStyles.marginRight = getSpacingValue(mr);
  if (mx !== undefined) { generatedStyles.marginLeft = getSpacingValue(mx); generatedStyles.marginRight = getSpacingValue(mx); }
  if (my !== undefined) { generatedStyles.marginTop = getSpacingValue(my); generatedStyles.marginBottom = getSpacingValue(my); }
  
  if (width !== undefined) generatedStyles.width = width;
  if (height !== undefined) generatedStyles.height = height;
  if (minWidth !== undefined) generatedStyles.minWidth = minWidth;
  if (minHeight !== undefined) generatedStyles.minHeight = minHeight;
  if (maxWidth !== undefined) generatedStyles.maxWidth = maxWidth;
  if (maxHeight !== undefined) generatedStyles.maxHeight = maxHeight;

  if (border) generatedStyles.border = border;
  if (borderTop) generatedStyles.borderTop = borderTop;
  if (borderBottom) generatedStyles.borderBottom = borderBottom;
  if (borderLeft) generatedStyles.borderLeft = borderLeft;
  if (borderRight) generatedStyles.borderRight = borderRight;
  if (borderColor) generatedStyles.borderColor = borderColor;
  if (borderWidth) generatedStyles.borderWidth = borderWidth;
  if (borderStyle) generatedStyles.borderStyle = borderStyle;
  if (borderRadius) generatedStyles.borderRadius = borderRadius;

  if (bg) generatedStyles.backgroundColor = bg;
  if (backgroundColor) generatedStyles.backgroundColor = backgroundColor;
  
  if (color) generatedStyles.color = color;

  if (flex !== undefined) generatedStyles.flex = flex;
  if (flexGrow !== undefined) generatedStyles.flexGrow = flexGrow;
  if (flexShrink !== undefined) generatedStyles.flexShrink = flexShrink;
  if (flexBasis !== undefined) generatedStyles.flexBasis = flexBasis;
  // --- FIX: Add the condition to handle the alignSelf prop ---
  if (alignSelf !== undefined) generatedStyles.alignSelf = alignSelf;

  if (position) generatedStyles.position = position;
  if (top !== undefined) generatedStyles.top = top;
  if (bottom !== undefined) generatedStyles.bottom = bottom;
  if (left !== undefined) generatedStyles.left = left;
  if (right !== undefined) generatedStyles.right = right;
  if (zIndex !== undefined) generatedStyles.zIndex = zIndex;

  if (overflow) generatedStyles.overflow = overflow;
  if (overflowX) generatedStyles.overflowX = overflowX;
  if (overflowY) generatedStyles.overflowY = overflowY;
  
  if (boxShadow) generatedStyles.boxShadow = boxShadow;
  if (textAlign) generatedStyles.textAlign = textAlign;

  const validChildren = React.Children.toArray(children).filter(Boolean);

  return (
    <Component
      ref={ref}
      className={`${styles.stackBase} ${className}`}
      style={{ ...generatedStyles, ...incomingStyle }}
      {...htmlAttributes}
    >
      {divider && validChildren.length > 1
        ? validChildren.map((child, index) => (
            <React.Fragment key={index}>
              {child}
              {index < validChildren.length - 1 && (
                <div className={styles.dividerWrapper} aria-hidden="true">
                  {divider}
                </div>
              )}
            </React.Fragment>
          ))
        : children}
    </Component>
  );
});

export const HStack = React.forwardRef((props, ref) => (
  <Stack direction="row" ref={ref} {...props} />
));

export default Stack;